import { Banner, BREAKPOINTS, LifeGlobalStyles, LoadingIndicator, Size, SiteFooter, SystemIcons } from '@laerdal/life-react-components';
import { useEffect, useRef, useState } from 'react';
import { Route, useLocation, useNavigate } from 'react-router';
import styled, { createGlobalStyle } from 'styled-components';
import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import ArticlePage from './pages/articlePage/ArticlePage';
import ArticleSearchPage from './pages/articleSearchPage/ArticleSearchPage';
import LandingPage from './pages/landingPage/LandingPage';
import { Routes } from 'react-router-dom';
import Header from './commonComponents/Header';
import posthog from 'posthog-js';
import ProductCategoryPage from './pages/landingPage/ProductCategoryPage';
import ProductLinePage from './pages/landingPage/ProductLinePage';
import ProductLinePage3 from './pages/landingPage/ProductLinePage3';
import ProductLineArticlesPage from './pages/landingPage/ProductLineArticlesPage';
import ProductLineUserGuidePage from './pages/landingPage/ProductLineUserGuidePage';
import ProductLineUserGuidePage2 from './pages/landingPage/ProductLineUserGuidePage2';
import ProductLineUserGuidePage3 from './pages/landingPage/ProductLineUserGuidePage3';
import backend from "i18next-http-backend";
import React from 'react';
import LanguageSelector from './commonComponents/Language';
import { useDispatch, useSelector } from 'react-redux';
import { setMarket, selectMarket, selectLocale, setTranslateWithAi, setTranslateWithAiMarket } from './store/slices/globalConfigSlice';
import { selectUserProfile, setAuth0Account, setUserProfile } from './store/slices/userSlice';
import { marketList } from './model/constants/marketContants';
import { getPathWithoutMarket, getUrlMarket } from './model/constants/marketUrl';
import UserApi from './services/api/UserApi';
import AuthApi from './services/api/AuthApi';
import { User } from './model/dto/userProfile/user';
import ProductRedirectPage from './pages/landingPage/ProductRedirectPage';
import { onConsentChanged } from './services/consent';
import { applyConsentState } from './services/analytics';

// Lazy routes: mid-session pages users rarely deep-link into. Entry pages
// (landing, product, article, search) stay eager so deep links render without
// an extra chunk round trip. webpackPrefetch warms likely-next chunks on idle.
const LoginCallbackPage = React.lazy(() => import('./pages/login/LoginCallbackPage'));
const LoginPage = React.lazy(() => import('./pages/login/LoginPage'));
const SimcapturePage = React.lazy(() => import('./pages/simcapture/simcapturePage'));
const DownloadsPage = React.lazy(() => import(/* webpackPrefetch: true */ './pages/downloadsPage/DownloadsPage'));
const QcprSupportPage = React.lazy(() => import('./pages/qcprSupport/QcprSupportPage'));
const QcprSupportTroubleshootingPage = React.lazy(() => import('./pages/qcprSupport/QcprSupportTroubleshootingPage'));
const QcprSupportSuggestionsPage = React.lazy(() => import('./pages/qcprSupport/QcprSupportSuggestionsPage'));
const QcprSupportSuggestionsCreatedPage = React.lazy(() => import('./pages/qcprSupport/QcprSupportSuggestionsCreatedPage'));
const OnboardingPage = React.lazy(() => import('./pages/onboardingPage/OnboardingPage'));
const OnboardingRedirectPage = React.lazy(() => import('./pages/onboardingPage/OnboardingRedirectPage'));
const EmbededSearchPage = React.lazy(() => import('./pages/embededSearchPage/EmbededSearchPage'));
const EmbededArticlePage = React.lazy(() => import('./pages/embededArticlePage/EmbededArticlePage'));
const AssetRedirectPage = React.lazy(() => import('./pages/asset/AssetRedirectPage'));
const CaseCreatePage = React.lazy(() => import(/* webpackPrefetch: true */ './pages/cases/CaseCreatePage'));
const CaseSuboptionsPage = React.lazy(() => import('./pages/cases/CaseSuboptionsPage'));
const CaseAssetSelectPage = React.lazy(() => import('./pages/cases/CaseAssetSelectPage'));
const CaseAssetInfoPage = React.lazy(() => import('./pages/cases/CaseAssetInfoPage'));
const CaseAssetInfoPageAI = React.lazy(() => import('./pages/cases/CaseAssetInfoPageAI'));
const CaseFormPageRefactored = React.lazy(() => import('./pages/cases/CaseFormPageRefactored'));
const CaseConfirmationPage = React.lazy(() => import('./pages/cases/CaseConfirmationPage'));

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-y: auto;
`;

const GlobalStyleOverrides = createGlobalStyle`
  h1, h2, h3, h4, h5, h6, p, span {
    max-width: none;
  }
`;

const FooterContainer = styled.div`
  margin-top: auto;
`;

const StickyBanner = styled(Banner)`
  margin: 0 !important;
  position: sticky;
  top: 0;
  z-index: 1000;
  min-height: unset;

  div {
    min-height: unset;

    div {
      min-height: unset;
    }
  }

  & > div  {
    margin: 0 16px !important;

    ${BREAKPOINTS.MEDIUM} {
      margin:  0 64px !important;

    }
  }
    
  & > div > div > div  {
    font-size: 14px !important;

    ${BREAKPOINTS.MEDIUM} {
      font-size: 20px !important;
    }
  }
`;


const LoadingContainer = styled.div`
  padding: 50px;
`;


// Initialize i18next
i18next
  .use(backend)
  .use(initReactI18next)
  //@ts-ignore
  .init({
    lng: 'en',
    debug: false,
    load: 'currentOnly',
    cleanCode: true,
    keySeparator: false,
    fallbackLng: 'en',
    compatibilityJSON: 'v3',
    defaultNS: 'Common',
    interpolation: {
      escapeValue: false,
      format: function (value: any, format: any, lng: any) {
        // if (value instanceof Date) {
        //   return moment(value).format(format);
        // }
        return value;
      },
    },
    react: {
      useSuspense: true,
    },
    nonExplicitSupportedLngs: false,
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json'
    }
  });

// Market resolution helpers (URL helpers live in ./model/constants/marketUrl)
const getCookieMarket = (): string => {
  const lngCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('lng='))
    ?.split('=')[1];
  if (lngCookie === 'en') return 'int';
  return lngCookie || '';
};

const getUserMarket = (user: User | undefined): string => {
  return user?.currentOrganization?.services
    ?.find(x => !!x.commerceMarket)
    ?.commerceMarket?.code?.toLowerCase() ?? '';
};

const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const currentLocale = useSelector(selectLocale);
  const currentMartket = useSelector(selectMarket);
  const navigate = useNavigate();
  const { t } = useTranslation(['translation', 'cases', 'landingPage']);
  const [loadingApp, setLoadingApp] = useState<boolean>(true);
  const [showHeaderFooter, setShowHeaderFooter] = useState<boolean>(true);
  const [initializationResolved, setInitializationResolved] = useState<boolean>(false);
  const initialResolveDoneRef = useRef(false);
  const authCheckedRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const user = useSelector(selectUserProfile);
  // Latest user for callbacks that outlive the render (consent changes)
  const userRef = useRef<User | undefined>(user);
  userRef.current = user;

  useEffect(() => {
    i18next.changeLanguage(currentLocale);
  }, [currentLocale]);

  window.addEventListener("message",
    (event) => {
      if (event.data == 'closingHelpU') {
        // @ts-ignore
        posthog?.capture('helpU closed', {
          $geoip_disable: true,
        });
      }
    },
    false
  );

  const handlePageChange = () => {
    // @ts-ignore
    let timeSpent = Math.floor((Date.now() - window.supportStartTime) / 1000);
    posthog.capture('SUPP-PageLeave',
      {
        seconds_on_page: timeSpent,
        //@ts-ignore
        url: window.supportFullUrl
      }
    );


    // @ts-ignore
    window.supportStartTime = new Date().getTime();
    // @ts-ignore
    window.supportFullUrl = window.location.href;
  };

  const onLoginHandler = (userInfo: any) => {
    if (!userInfo) return;
    
    dispatch(
      setAuth0Account({
        email: userInfo.email,
        firstName: userInfo.given_name ?? userInfo.name ?? '',
        identityId: userInfo.sub,
        lastName: userInfo.family_name ?? '',
        locale: userInfo.locale ?? 'en',
      }),
    );

    return UserApi.getUser()
      .then((user) => {
        dispatch(setUserProfile(user));
      })
  };

  useEffect(() => {
    // Skip auth check on the callback page — LoginCallbackPage handles the redirect
    if (location.pathname.includes('/login/callback')) {
      setLoadingApp(false);
      return;
    }

    // Embedded pages are anonymous — never run the Auth0 silent session check
    if (location.pathname.includes('/embeded')) {
      setLoadingApp(false);
      return;
    }

    // Only check auth once (after callback navigates here, or on direct load)
    if (authCheckedRef.current) return;
    authCheckedRef.current = true;

    // Initialize Auth0 session check
    const initAuth = async () => {
      try {
        const isAuth = await AuthApi.verifySession();
        if (isAuth) {
          const userInfo = await AuthApi.retrieveAccountInfo();
          if (userInfo) {
            await onLoginHandler(userInfo);
          }
        }
      } catch (err) {
        console.error('Auth0 init error:', err);
      } finally {
        setLoadingApp(false);
      }
    };

    initAuth();
  }, [location.pathname]);

  // Initialization: runs once after auth check completes
  // Resolves market (user > cookie > URL > 'int') then loads i18n locale files
  useEffect(() => {
    if (loadingApp || initialResolveDoneRef.current) return;
    initialResolveDoneRef.current = true;

    // Embedded pages: skip market resolution/redirect, use the locale query param
    if (location.pathname.includes('/embeded')) {
      const embedLocale = new URLSearchParams(location.search).get('locale') || 'en';
      i18next
        .changeLanguage(embedLocale)
        .then(() => i18next.loadNamespaces(['translation', 'cases', 'landingPage', 'Common']))
        .finally(() => {
          setInitializationResolved(true);
        });
      return;
    }

    const urlMarket = getUrlMarket(location.pathname);
    const cookieMarket = getCookieMarket();
    const userMarket = getUserMarket(user);

    let resolvedMarket: string;

    if (user && userMarket) {
      // Priority 1: Logged-in user's market
      resolvedMarket = userMarket;
    } else if (cookieMarket) {
      // Priority 2: Cookie market (when not logged in)
      resolvedMarket = cookieMarket;
    } else {
      // Priority 3: URL market, fallback to 'int'
      resolvedMarket = urlMarket || 'int';
    }

    // Validate market exists in marketList
    if (!marketList[resolvedMarket]) {
      resolvedMarket = 'int';
    }

    dispatch(setMarket(resolvedMarket));

    // Navigate if resolved market differs from URL market
    if (resolvedMarket !== urlMarket) {
      const pathWithoutMarket = getPathWithoutMarket(location.pathname);
      navigate(`/${resolvedMarket}${pathWithoutMarket}${location.search}${location.hash}`, { replace: true });
    }

    // Restore AI translation cookies
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [name, value] = cookie.split('=').map(c => c.trim());
      acc[name] = value;
      return acc;
    }, {} as Record<string, string>);

    if (resolvedMarket && cookies['laerdalsupport-market'] === resolvedMarket) {
      dispatch(setTranslateWithAi(cookies['laerdalsupport-translate-with-ai'] === 'true'));
      dispatch(setTranslateWithAiMarket(cookies['laerdalsupport-market'] ?? ''));
    } else {
      dispatch(setTranslateWithAi(false));
      dispatch(setTranslateWithAiMarket(''));
    }

    // Load i18n locale files for the resolved market, then mark initialization complete
    const resolvedLocale = marketList[resolvedMarket]?.locale ?? 'en';
    i18next
      .changeLanguage(resolvedLocale)
      .then(() => i18next.loadNamespaces(['translation', 'cases', 'landingPage', 'Common']))
      .finally(() => {
        setInitializationResolved(true);
      });
  }, [loadingApp]);

  // Handle user login after initial market resolution (e.g., user clicks Login)
  useEffect(() => {
    if (!initialResolveDoneRef.current || !user) return;

    const userMarket = getUserMarket(user);
    if (userMarket && userMarket !== currentMartket) {
      dispatch(setMarket(userMarket));
      const pathWithoutMarket = getPathWithoutMarket(location.pathname);
      navigate(`/${userMarket}${pathWithoutMarket}${location.search}${location.hash}`, { replace: true });
    }
  }, [user]);

  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
    posthog.capture('$pageview')
    handlePageChange();
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener('beforeunload', handlePageChange);

    return () => {
      window.removeEventListener('beforeunload', handlePageChange);
    };
  }, []);

  useEffect(() => {
    const show = !location.pathname.startsWith('/embeded')
      && !location.pathname.startsWith(`/${currentMartket}/embeded`)
      && !location.pathname.startsWith('/simcapture')
      && !location.pathname.startsWith(`/${currentMartket}/simcapture`);

    setShowHeaderFooter(show);

  }, [currentMartket, location.pathname]);

  // Identify (or stay anonymous) whenever the user changes
  useEffect(() => {
    applyConsentState(user);
  }, [user]);

  // Reapply when the user answers/changes the OneTrust banner
  useEffect(() => {
    const unsubscribe = onConsentChanged(() => applyConsentState(userRef.current));
    return unsubscribe;
  }, []);

  // Show loader until auth check, market resolution and i18n loading are complete
  if (!initializationResolved) {
    return (
      <>
        <LifeGlobalStyles />
        <GlobalStyleOverrides />
        <ContentContainer ref={contentRef}>
          <LoadingContainer><LoadingIndicator /></LoadingContainer>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <LifeGlobalStyles />
      <GlobalStyleOverrides />
      
      
        <ContentContainer ref={contentRef}>
          {showHeaderFooter &&
            <>
              <LanguageSelector />
              <Header />
              
              
            </>
          }

        <React.Suspense fallback={<LoadingContainer><LoadingIndicator /></LoadingContainer>}>

          <Routes>
            <Route path='/:market?/login/callback' element={<LoginCallbackPage />} />
            <Route path='/:market?/login' element={<LoginPage />} />
            <Route path='/:market?/simcapture' element={<SimcapturePage />} ></Route>

            <Route path='/:market?/' element={<LandingPage />} />

            <Route path='/:market?/downloads' element={<DownloadsPage />} />
            {/* <Route path='/:market?/downloads/:productLineCode' element={<DownloadsPage />} /> */}
            <Route path='/:market?/qcpr-support' element={<QcprSupportPage />} />
            <Route path='/:market?/qcpr-support/troubleshooting' element={<QcprSupportTroubleshootingPage />} />
            <Route path='/:market?/qcpr-support/suggestions' element={<QcprSupportSuggestionsPage />} />
            <Route path='/:market?/qcpr-support/suggestions/created' element={<QcprSupportSuggestionsCreatedPage />} />

            <Route path='/:market?/product/:productLink' element={<ProductRedirectPage />} />
            <Route path='/:market?/onboarding/:productLink' element={<OnboardingRedirectPage />} />

            <Route path='/:market?/category/:categoryLink' element={<ProductCategoryPage />} />
            <Route path='/:market?/category/:categoryLink' element={<ProductCategoryPage />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink' element={<ProductLinePage />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink/3' element={<ProductLinePage3 />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink/onboarding' element={<OnboardingPage />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink/userguide' element={<ProductLineUserGuidePage />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink/userguide2' element={<ProductLineUserGuidePage2 />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink/userguide3' element={<ProductLineUserGuidePage3 />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink/downloads' element={<DownloadsPage />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink/component/:componentLink' element={<ProductLinePage />} />
            <Route path='/:market?/category/:categoryLink/product/:productLink/articles/:componentLink' element={<ProductLineArticlesPage />} />

            <Route path='/:market?/articles' element={<ArticleSearchPage />} />
            <Route path='/:market?/embeded/search' element={<EmbededSearchPage />} />
            <Route path='/:market?/embeded/articles/:articleNumber' element={<EmbededArticlePage />} />

            <Route path='/:market?/articles/:articleNumber' element={<ArticlePage />} />
            <Route path='/:market?/articles/Knowledge/:title' element={<ArticlePage />} />

            <Route path='/:market?/asset-redirect' element={<AssetRedirectPage />} />

            <Route path='/:market?/case/create' element={<CaseCreatePage />} />
            <Route path='/:market?/case/create/:option' element={<CaseSuboptionsPage />} />
            {/* <Route path='/:market?/case/create/:option/:suboption' element={<CaseFormPage />} /> */}
            <Route path='/:market?/case/create/technical/assetSelect' element={<CaseAssetSelectPage />} />
            {/* @ts-ignore */}
            { process.env?.REACT_APP_AI_DEFLECTION_ENABLED == 'true' &&
              <Route path='/:market?/case/create/technical/assetSelect/info' element={<CaseAssetInfoPageAI />} />
            }
            {/* @ts-ignore */}
            { process.env?.REACT_APP_AI_DEFLECTION_ENABLED !== 'true' &&
              <Route path='/:market?/case/create/technical/assetSelect/info' element={<CaseAssetInfoPage />} />
            }
            
            <Route path='/:market?/case/create/:option/:suboption' element={<CaseFormPageRefactored />} />

            <Route path='/:market?/case/create/confirmation/:caseNumber' element={<CaseConfirmationPage />} />
          </Routes>
        </React.Suspense>
          {showHeaderFooter
            &&
            <FooterContainer>
              <SiteFooter
                id="helpu-footer"
                toTopText={t('TOP', { ns: 'cases' })}
                sitename='Support'
                copyrightText={
                  t(`Copyright © {{year}} Laerdal Medical. All Rights Reserved.`,
                    { ns: 'landingPage', year: new Date().getFullYear() })
                }
                bottomLinks={[
                  {
                    label: t('Terms of Use', { ns: 'landingPage' }),
                    to: 'https://www.laerdal.com/support/customer-service/terms-of-use/',
                  },
                  {
                    label: t('Privacy Statement', { ns: 'landingPage' }),
                    to: 'https://www.laerdal.com/support/customer-service/privacy-statement/',
                  },
                  {
                    label: t('Accessibility statement', { ns: 'landingPage' }),
                    to: 'https://www.laerdal.com/support/customer-service/accessibility/',
                  },
                  {
                    label: t('Sustainability', { ns: 'landingPage' }),
                    to: 'https://www.laerdal.com/about-us/sustainability/',
                  },
                  {
                    label: t('Contact Us', { ns: 'landingPage' }),
                    to: '/case/create',
                  }
                ]}
              />
            </FooterContainer>
          }
        </ContentContainer>
    </>
  );
}

export default App;